import { Directory, File, Paths } from 'expo-file-system';
import { Platform } from 'react-native';

/**
 * Offline storage layout (inside the app's Documents container, which iOS
 * never purges on its own — unlike Caches):
 *
 *   Documents/offline-videos/videos/<lessonVideoId>.mp4
 *   Documents/offline-videos/captions/<lessonVideoId>.vtt
 *   Documents/offline-videos/artwork/<lessonVideoId>.jpg
 *   Documents/offline-videos/snapshots/<courseSlug>__<lessonSlug>.json
 *
 * Records store paths relative to the root; resolve them at use time.
 */
export const OFFLINE_ROOT_DIR = 'offline-videos';

export function offlineRoot(): Directory {
  return new Directory(Paths.document, OFFLINE_ROOT_DIR);
}

export function ensureOfflineDirs(): void {
  const root = offlineRoot();
  for (const dir of [
    root,
    new Directory(root, 'videos'),
    new Directory(root, 'captions'),
    new Directory(root, 'artwork'),
    new Directory(root, 'snapshots'),
  ]) {
    if (!dir.exists) dir.create({ intermediates: true, idempotent: true });
  }
}

export function videoRelativePath(lessonVideoId: number): string {
  return `videos/${lessonVideoId}.mp4`;
}

export function captionsRelativePath(lessonVideoId: number): string {
  return `captions/${lessonVideoId}.vtt`;
}

export function artworkRelativePath(lessonVideoId: number): string {
  return `artwork/${lessonVideoId}.jpg`;
}

export function snapshotRelativePath(key: string): string {
  return `snapshots/${key}.json`;
}

/** `file://` URI for a relative offline path (expo-video, expo-file-system). */
export function offlineFileUri(relativePath: string): string {
  return new File(offlineRoot(), relativePath).uri;
}

/**
 * Plain filesystem path (no `file://`) for the background downloader's
 * `destination`, which expects a POSIX path on both platforms.
 */
export function offlineFilePath(relativePath: string): string {
  const uri = offlineFileUri(relativePath);
  const path = uri.startsWith('file://') ? uri.slice('file://'.length) : uri;
  return Platform.OS === 'android' ? path : decodeURIComponent(path);
}

export function offlineFileExists(relativePath: string): boolean {
  try {
    return new File(offlineRoot(), relativePath).exists;
  } catch {
    return false;
  }
}

export function offlineFileSize(relativePath: string): number | null {
  try {
    const file = new File(offlineRoot(), relativePath);
    return file.exists ? file.size : null;
  } catch {
    return null;
  }
}

export function deleteOfflineFile(relativePath: string): void {
  try {
    const file = new File(offlineRoot(), relativePath);
    if (file.exists) file.delete();
  } catch {
    // Missing file is the desired end state.
  }
}

export function readOfflineText(relativePath: string): string | null {
  try {
    const file = new File(offlineRoot(), relativePath);
    return file.exists ? file.textSync() : null;
  } catch {
    return null;
  }
}

export function writeOfflineText(relativePath: string, content: string): void {
  ensureOfflineDirs();
  const file = new File(offlineRoot(), relativePath);
  file.write(content);
}

export function snapshotKeyFor(courseSlug: string, lessonSlug: string): string {
  const safe = (s: string) => s.replace(/[^a-z0-9-]/gi, '_');
  return `${safe(courseSlug)}__${safe(lessonSlug)}`;
}
